import React from 'react';

// Site-wide styled tooltip, replacing the browser's native `title` bubble on
// public pages. Pure CSS: it shows on hover (after a short delay, so it doesn't
// flash while the pointer just passes through) and on keyboard focus - not on
// a mouse click's focus, which would leave it stuck open.
// The wrapper is a positioned inline-flex box around `children`; pass the
// child's own layout classes (margins, max-width...) via `className`, since
// the wrapper is now what sits in the parent's layout.

const PLACEMENT_CLASSES = {
  top: 'bottom-full left-1/2 mb-2 -translate-x-1/2',
  bottom: 'top-full left-1/2 mt-2 -translate-x-1/2',
  'bottom-end': 'top-full right-0 mt-2',
  left: 'right-full top-1/2 mr-2 -translate-y-1/2',
  right: 'left-full top-1/2 ml-2 -translate-y-1/2',
};

const ARROW_CLASSES = {
  top: 'top-full left-1/2 -mt-1 -translate-x-1/2 border-b border-r',
  bottom: 'bottom-full left-1/2 -mb-1 -translate-x-1/2 border-t border-l',
  'bottom-end': 'bottom-full right-3 -mb-1 border-t border-l',
  left: 'left-full top-1/2 -ml-1 -translate-y-1/2 border-t border-r',
  right: 'right-full top-1/2 -mr-1 -translate-y-1/2 border-b border-l',
};

export default function Tooltip({ label, placement = 'top', wrap = false, className = '', children }) {
  if (!label) return children;

  // The wrapper must be positioned to anchor the bubble, but a caller that
  // positions it itself (e.g. `absolute top-3 right-3`) must win - with both
  // classes present, Tailwind's `relative` would silently override `absolute`.
  const positionClass = /\b(absolute|fixed|sticky)\b/.test(className) ? '' : 'relative';

  return (
    <span className={`group/tip ${positionClass} inline-flex ${className}`}>
      {children}
      <span
        role="tooltip"
        className={`pointer-events-none absolute z-40 rounded-lg border border-white/15 bg-slate-950/95 px-2.5 py-1.5 text-xs font-semibold leading-snug text-white shadow-xl shadow-black/40 opacity-0 transition-opacity duration-150 group-hover/tip:opacity-100 group-hover/tip:delay-150 group-has-[:focus-visible]/tip:opacity-100 ${wrap ? 'w-max max-w-[8rem]' : 'whitespace-nowrap'} ${PLACEMENT_CLASSES[placement]}`}
      >
        {label}
        <span aria-hidden="true" className={`absolute h-2 w-2 rotate-45 border-white/15 bg-slate-950 ${ARROW_CLASSES[placement]}`} />
      </span>
    </span>
  );
}
